import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { Loader2, Plus, Edit, Trash2, Zap, Globe2, Mail } from 'lucide-react';
import { preventImplicitSubmit } from '../utils/form';

const emptyForm = {
  name: '',
  eventKey: 'request.created',
  type: 'email',
  isActive: true,
  isGlobal: false,
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  body: '',
  bodyHtml: '',
  from: '',
  formIds: []
};

const splitList = (value) => {
  if (!value) return [];
  return value
    .split(/[\n,;]/g)
    .map(v => v.trim())
    .filter(Boolean);
};

const joinList = (value) => {
  if (!Array.isArray(value)) return '';
  return value.join(', ');
};

export default function AdminTriggers() {
  const [triggers, setTriggers] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [formState, setFormState] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [triggersRes, formsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/triggers`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API_BASE_URL}/api/forms`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (triggersRes.ok) {
        const data = await triggersRes.json();
        setTriggers(data || []);
      }
      if (formsRes.ok) {
        const data = await formsRes.json();
        setForms(data || []);
      }
    } catch (e) {
      setTriggers([]);
    } finally {
      setLoading(false);
    }
  };

  const eventOptions = useMemo(() => {
    const fromData = triggers.map(t => t.eventKey).filter(Boolean);
    const base = ['request.created', 'request.assigned', 'request.edited', 'request.completed'];
    const merged = Array.from(new Set([...base, ...fromData]));
    return merged;
  }, [triggers]);

  const filteredTriggers = triggers.filter(t => {
    const matchesSearch = (t.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.eventKey || '').toLowerCase().includes(search.toLowerCase());
    const matchesEvent = eventFilter === 'all' || t.eventKey === eventFilter;
    return matchesSearch && matchesEvent;
  });

  const startCreate = () => {
    setEditingId(null);
    setFormState(emptyForm);
  };

  const startEdit = (trigger) => {
    let config = {};
    try {
      config = typeof trigger.config === 'string' ? JSON.parse(trigger.config) : (trigger.config || {});
    } catch (e) {
      config = {};
    }
    setEditingId(trigger.id);
    setFormState({
      name: trigger.name || '',
      eventKey: trigger.eventKey || 'request.created',
      type: trigger.type || 'email',
      isActive: !!trigger.isActive,
      isGlobal: !!trigger.isGlobal,
      to: joinList(config.to),
      cc: joinList(config.cc),
      bcc: joinList(config.bcc),
      subject: config.subject || '',
      body: config.body || '',
      bodyHtml: config.bodyHtml || '',
      from: config.from || '',
      formIds: Array.isArray(trigger.formIds) ? trigger.formIds : []
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trigger?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/triggers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) loadData();
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formState.name,
      eventKey: formState.eventKey,
      type: formState.type,
      isActive: formState.isActive,
      isGlobal: formState.isGlobal,
      config: {
        to: splitList(formState.to),
        cc: splitList(formState.cc),
        bcc: splitList(formState.bcc),
        subject: formState.subject,
        body: formState.body,
        bodyHtml: formState.bodyHtml,
        from: formState.from
      }
    };

    try {
      const url = editingId ? `${API_BASE_URL}/api/triggers/${editingId}` : `${API_BASE_URL}/api/triggers`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) return;
      let triggerId = editingId;
      if (!editingId) {
        const data = await res.json();
        triggerId = data.id;
      }

      if (triggerId) {
        await fetch(`${API_BASE_URL}/api/triggers/${triggerId}/assignments`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ formIds: formState.isGlobal ? [] : formState.formIds })
        });
      }

      setEditingId(null);
      setFormState(emptyForm);
      loadData();
    } catch (e) {}
  };

  const toggleForm = (formId) => {
    setFormState(prev => {
      const exists = prev.formIds.includes(formId);
      return {
        ...prev,
        formIds: exists ? prev.formIds.filter(id => id !== formId) : [...prev.formIds, formId]
      };
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormState(emptyForm);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader2 size={48} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="triggers-shell">
      <div className="triggers-header">
        <div>
          <h1>Automation Triggers</h1>
          <p className="dashboard-subtitle">Create event driven automations for approvals</p>
        </div>
        <button className="button-primary button-lg" onClick={startCreate}>
          <Plus size={16} style={{ marginRight: '4px' }} /> New Trigger
        </button>
      </div>

      <div className="triggers-layout">
        <div className="triggers-panel">
          <div className="dashboard-filters" style={{ marginBottom: 16 }}>
            <input
              type="text"
              className="search-input"
              placeholder="Search triggers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="trigger-select"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
            >
              <option value="all">All events</option>
              {eventOptions.map(key => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </div>

          {filteredTriggers.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 24px' }}>
              <Zap size={44} />
              <h3>No triggers yet</h3>
              <p>Create a trigger to automate notifications</p>
            </div>
          ) : (
            <div className="triggers-grid">
              {filteredTriggers.map(trigger => (
                <div key={trigger.id} className="trigger-card">
                  <div className="trigger-card-header">
                    <div className="trigger-card-title">
                      <span className="trigger-icon"><Zap size={18} /></span>
                      <div>
                        <div className="trigger-name">{trigger.name}</div>
                        <div className="trigger-meta">{trigger.eventKey}</div>
                      </div>
                    </div>
                    <div className="trigger-actions">
                      <button className="action-btn" onClick={() => startEdit(trigger)} title="Edit">
                        <Edit size={16} />
                      </button>
                      <button className="action-btn danger" onClick={() => handleDelete(trigger.id)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="trigger-card-body">
                    <div className="trigger-badges">
                      <span className={`status-pill ${trigger.isActive ? 'active' : 'inactive'}`}>
                        {trigger.isActive ? 'Active' : 'Paused'}
                      </span>
                      <span className="status-pill">
                        <Mail size={12} style={{ marginRight: 6 }} /> {trigger.type}
                      </span>
                      <span className="status-pill">
                        <Globe2 size={12} style={{ marginRight: 6 }} /> {trigger.isGlobal ? 'All forms' : 'Assigned'}
                      </span>
                    </div>
                    {!trigger.isGlobal && (
                      <div className="trigger-forms">
                        {trigger.formIds?.length || 0} form(s) assigned
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="triggers-editor">
          <div className="triggers-editor-card">
            <div className="triggers-editor-header">
              <div className="triggers-editor-title">{editingId ? 'Edit Trigger' : 'Create Trigger'}</div>
              {editingId && (
                <button className="button-outline button-sm" onClick={resetForm}>New</button>
              )}
            </div>
            <form onSubmit={handleSubmit} onKeyDown={preventImplicitSubmit} className="triggers-form">
              <div className="form-grid two-col">
                <div className="form-field">
                  <label>Trigger Name</label>
                  <input
                    required
                    type="text"
                    value={formState.name}
                    onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>Event Key</label>
                  <input
                    required
                    type="text"
                    value={formState.eventKey}
                    onChange={(e) => setFormState(prev => ({ ...prev, eventKey: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-grid two-col">
                <div className="form-field">
                  <label>Type</label>
                  <select
                    value={formState.type}
                    onChange={(e) => setFormState(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="email">Email</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <div className="toggle-row">
                    <button
                      type="button"
                      className={`toggle-btn ${formState.isActive ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isActive: true }))}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${!formState.isActive ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isActive: false }))}
                    >
                      Paused
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-grid two-col">
                <div className="form-field">
                  <label>Scope</label>
                  <div className="toggle-row">
                    <button
                      type="button"
                      className={`toggle-btn ${formState.isGlobal ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isGlobal: true, formIds: [] }))}
                    >
                      All Forms
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${!formState.isGlobal ? 'active' : ''}`}
                      onClick={() => setFormState(prev => ({ ...prev, isGlobal: false }))}
                    >
                      Assigned Forms
                    </button>
                  </div>
                </div>
                <div className="form-field">
                  <label>Sender</label>
                  <input
                    type="text"
                    value={formState.from}
                    onChange={(e) => setFormState(prev => ({ ...prev, from: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>

              {!formState.isGlobal && (
                <div className="form-field">
                  <label>Assign Forms</label>
                  <div className="pill-choices">
                    {forms.map(form => (
                      <button
                        key={form.id}
                        type="button"
                        className={formState.formIds.includes(form.id) ? 'pill active' : 'pill'}
                        onClick={() => toggleForm(form.id)}
                      >
                        {form.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-field">
                <label>To</label>
                <input
                  type="text"
                  value={formState.to}
                  onChange={(e) => setFormState(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="Comma separated emails or templates"
                />
              </div>

              <div className="form-grid two-col">
                <div className="form-field">
                  <label>CC</label>
                  <input
                    type="text"
                    value={formState.cc}
                    onChange={(e) => setFormState(prev => ({ ...prev, cc: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>BCC</label>
                  <input
                    type="text"
                    value={formState.bcc}
                    onChange={(e) => setFormState(prev => ({ ...prev, bcc: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Subject</label>
                <input
                  type="text"
                  value={formState.subject}
                  onChange={(e) => setFormState(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>

              <div className="form-field">
                <label>Body</label>
                <textarea
                  rows={4}
                  value={formState.body}
                  onChange={(e) => setFormState(prev => ({ ...prev, body: e.target.value }))}
                />
              </div>

              <div className="form-field">
                <label>Body HTML</label>
                <textarea
                  rows={4}
                  value={formState.bodyHtml}
                  onChange={(e) => setFormState(prev => ({ ...prev, bodyHtml: e.target.value }))}
                />
              </div>

              <div className="triggers-footer">
                <button type="submit" className="button-primary">
                  {editingId ? 'Update Trigger' : 'Create Trigger'}
                </button>
                <button type="button" className="button-outline" onClick={resetForm}>
                  Clear
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
